import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { AssistantProvider } from '@/context/AssistantContext';
import AppShell from '@/components/AppShell';
import { Toaster } from '@/components/ui/sonner';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import AdminConsole from '@/pages/AdminConsole';
import Beranda from '@/pages/Beranda';
import Pelanggan from '@/pages/Pelanggan';
import PelangganDetail from '@/pages/PelangganDetail';
import Katalog from '@/pages/Katalog';
import Penawaran from '@/pages/Penawaran';
import PenawaranBuilder from '@/pages/PenawaranBuilder';
import Proyek from '@/pages/Proyek';
import ProyekDetail from '@/pages/ProyekDetail';
import Pembayaran from '@/pages/Pembayaran';
import Pengaturan from '@/pages/Pengaturan';
import Optimasi from '@/pages/Optimasi';
import FollowUp from '@/pages/FollowUp';
import WhatsAppInbox from '@/pages/WhatsAppInbox';
import TugasSaya from '@/pages/TugasSaya';
import Inventory from '@/pages/Inventory';
import Laporan from '@/pages/Laporan';
import Billing from '@/pages/Billing';

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function homePathFor(role) {
  if (role === 'super_admin') return '/admin';
  if (role === 'field') return '/tugas';
  return '/beranda';
}

// Public marketing landing for anonymous; authenticated users are routed to their app.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={homePathFor(user.role)} replace />;
  return <Landing />;
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/admin" replace />;
  return (
    <AssistantProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </AssistantProvider>
  );
}

function AdminRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/" replace />;
  return <AdminConsole />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<AdminRoute />} />
          <Route element={<ProtectedLayout />}>
            <Route path="/beranda" element={<Beranda />} />
            <Route path="/pelanggan" element={<Pelanggan />} />
            <Route path="/pelanggan/:id" element={<PelangganDetail />} />
            <Route path="/katalog" element={<Katalog />} />
            <Route path="/penawaran" element={<Penawaran />} />
            <Route path="/penawaran/baru" element={<PenawaranBuilder />} />
            <Route path="/penawaran/:id" element={<PenawaranBuilder />} />
            <Route path="/proyek" element={<Proyek />} />
            <Route path="/proyek/:id" element={<ProyekDetail />} />
            <Route path="/pembayaran" element={<Pembayaran />} />
            <Route path="/pengaturan" element={<Pengaturan />} />
            <Route path="/langganan" element={<Billing />} />
            <Route path="/optimasi" element={<Optimasi />} />
            <Route path="/followup" element={<FollowUp />} />
            <Route path="/wa-inbox" element={<WhatsAppInbox />} />
            <Route path="/tugas" element={<TugasSaya />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/laporan" element={<Laporan />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
